import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Badge,
  Center,
  Container,
  Group,
  Loader,
  Pagination,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconSearch } from '@tabler/icons-react';

import { LogoPaper, PageTitle, SortableTh } from '@/components';
import { dueInfo } from '@/components/Inbox/queueFormat';
import { formatWhen } from '@/components/Playbooks/format';
import { formatUsd } from '@/components/Workbench/format';
import { useTeams } from '@/hooks';
import { useWorkbenchCases } from '@/hooks/useWorkbenchCases';
import type { WorkbenchCaseRow, WorkbenchCaseSort, WorkbenchCaseStage } from '@/types/workbench';

const PAGE_SIZE = 25;

const STAGE: Record<WorkbenchCaseStage, { label: string; color: string }> = {
  setup: { label: 'Setup', color: 'blue' },
  started: { label: 'Started', color: 'teal' },
  in_qa: { label: 'In QA', color: 'violet' },
  completed: { label: 'Completed', color: 'gray' },
  handed_back: { label: 'Handed back', color: 'orange' },
};

// Module-level so the Select gets the same array every render.
const STAGE_OPTIONS = (Object.keys(STAGE) as WorkbenchCaseStage[]).map((value) => ({
  value,
  label: STAGE[value].label,
}));

/** Open · finished · all, as the API's `done`. */
type Shown = 'open' | 'finished' | 'all';
const SHOWN_OPTIONS: { value: Shown; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'finished', label: 'Finished' },
  { value: 'all', label: 'All' },
];

const SORTS: ReadonlySet<string> = new Set<WorkbenchCaseSort>([
  'codename',
  'team',
  'stage',
  'due',
  'needs_you',
  'run',
  'spend',
  'started',
  'created',
]);

/** The case page, on the case's own item — null for a case with no family. */
export function workbenchCasePath(row: WorkbenchCaseRow): string | null {
  return row.familyKey
    ? `/cases/${encodeURIComponent(row.familyKey)}?item=rep:${row.reportId}`
    : null;
}

function Due({ row }: Readonly<{ row: WorkbenchCaseRow }>) {
  const due = dueInfo({ dueAt: row.due.dueAt, turnaroundDays: row.due.turnaroundDays });
  if (!due) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }
  return (
    <Tooltip label={`Due ${due.exact}${row.due.overridden ? ' (fixed date)' : ''}`} withArrow>
      <Badge
        size="sm"
        variant="light"
        color={due.tone === 'overdue' ? 'red' : due.tone === 'warning' ? 'orange' : 'gray'}
        style={{ textTransform: 'none' }}
      >
        {due.tone === 'overdue' ? due.label : `in ${due.label}`}
      </Badge>
    </Tooltip>
  );
}

function Run({ row }: Readonly<{ row: WorkbenchCaseRow }>) {
  if (row.run.status) {
    return (
      <Badge size="sm" variant="light" color="blue" style={{ textTransform: 'none' }}>
        {row.run.status === 'running' ? 'Running' : 'Queued'}
        {row.run.reason ? ` · ${row.run.reason.replace(/_/g, ' ')}` : ''}
      </Badge>
    );
  }
  return (
    <Text size="sm" c="dimmed">
      {row.run.lastStatementAt ? `Last ${formatWhen(row.run.lastStatementAt)}` : 'No run yet'}
    </Text>
  );
}

function Spend({ row }: Readonly<{ row: WorkbenchCaseRow }>) {
  const spent = formatUsd(row.threshold.spent) ?? '$0';
  const limit = row.threshold.usd === null ? null : formatUsd(row.threshold.usd);
  return (
    <Group gap={6} wrap="nowrap">
      <Text size="sm">{spent}</Text>
      {limit ? (
        <Badge
          size="sm"
          variant={row.threshold.reached ? 'filled' : 'outline'}
          color={row.threshold.reached ? 'orange' : 'gray'}
          style={{ textTransform: 'none' }}
        >
          {row.threshold.reached ? 'At the threshold' : `of ${limit}`}
        </Badge>
      ) : null}
    </Group>
  );
}

/**
 * The Workbench's cases: every live case, and the ones released to QA,
 * completed or handed back in the last thirty days (vNext-api
 * `docs/workbench.md`, "The case list"). Search, the filters, the order and
 * the paging run server-side, so a change of any resets to page 1. A row
 * opens the case.
 */
export function WorkbenchCasesPage() {
  const navigate = useNavigate();
  const { teams } = useTeams();
  const [page, setPage] = useState(1);
  const [shown, setShown] = useState<Shown>('all');
  const [stage, setStage] = useState<WorkbenchCaseStage | null>(null);
  const [teamId, setTeamId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 250);
  const [sortBy, setSortBy] = useState<WorkbenchCaseSort | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const { list, error, isLoading } = useWorkbenchCases({
    page,
    pageSize: PAGE_SIZE,
    q: debouncedSearch.trim() || undefined,
    stages: stage ? [stage] : undefined,
    done: shown === 'all' ? undefined : shown === 'finished',
    teamId: teamId ?? undefined,
    sort: sortBy ?? undefined,
    order: sortBy ? sortOrder : undefined,
  });
  const teamNames = useMemo(
    () => Object.fromEntries(teams.map((team) => [String(team.id), team.name])),
    [teams]
  );
  const teamOptions = useMemo(
    () =>
      teams
        .map((team) => ({ value: String(team.id), label: team.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [teams]
  );
  const resetPage = () => setPage(1);
  const onSort = (field: string) => {
    if (!SORTS.has(field)) {
      return;
    }
    const sort = field as WorkbenchCaseSort;
    if (sortBy === sort) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(sort);
      setSortOrder('asc');
    }
    resetPage();
  };
  const sortProps = { sortBy: sortBy ?? '', sortOrder, onSort };

  if (isLoading && !list) {
    return (
      <Center h={300}>
        <Loader />
      </Center>
    );
  }
  if (error && !list) {
    return (
      <Container size="xl" py="md">
        <Text c="dimmed">Not found.</Text>
      </Container>
    );
  }
  const rows = list?.items ?? [];
  const total = list?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const narrowed =
    Boolean(debouncedSearch.trim()) || stage !== null || teamId !== null || shown !== 'all';

  return (
    <Container size="xl" py="md">
      <Stack>
        <Stack gap={4}>
          <PageTitle title="Workbench cases" />
          <Text size="sm" c="dimmed" maw={760}>
            Every case in the Workbench, and the ones released, completed or handed back in the last
            thirty days.
          </Text>
        </Stack>

        <LogoPaper>
          <Stack>
            <Group align="flex-end">
              <TextInput
                label="Search"
                placeholder="Codename, playbook, report type…"
                leftSection={<IconSearch size={14} />}
                value={search}
                onChange={(e) => {
                  setSearch(e.currentTarget.value);
                  resetPage();
                }}
                data-testid="workbench-cases-search"
              />
              <Select
                label="Client"
                placeholder="All clients"
                data={teamOptions}
                value={teamId}
                onChange={(value) => {
                  setTeamId(value);
                  resetPage();
                }}
                searchable
                clearable
                data-testid="workbench-cases-client"
              />
              <Select
                label="Stage"
                placeholder="Any stage"
                data={STAGE_OPTIONS}
                value={stage}
                onChange={(value) => {
                  setStage(value as WorkbenchCaseStage | null);
                  resetPage();
                }}
                clearable
                data-testid="workbench-cases-stage"
              />
              <SegmentedControl
                value={shown}
                onChange={(value) => {
                  setShown(value as Shown);
                  resetPage();
                }}
                data={SHOWN_OPTIONS}
                data-testid="workbench-cases-shown"
              />
            </Group>

            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <SortableTh label="Case" field="codename" {...sortProps} />
                  <SortableTh label="Stage" field="stage" {...sortProps} />
                  <SortableTh label="Due" field="due" {...sortProps} />
                  <SortableTh label="Needs you" field="needs_you" {...sortProps} />
                  <SortableTh label="Run" field="run" {...sortProps} />
                  <SortableTh label="Spend" field="spend" {...sortProps} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={6}>
                      <Text size="sm" c="dimmed">
                        {narrowed ? 'No case matches.' : 'No case is in the Workbench.'}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : null}
                {rows.map((row) => {
                  const chip = STAGE[row.stage];
                  const path = workbenchCasePath(row);
                  return (
                    <Table.Tr
                      key={row.reportId}
                      style={{ cursor: path ? 'pointer' : undefined, opacity: row.done ? 0.65 : 1 }}
                      onClick={path ? () => navigate(path) : undefined}
                      data-testid={`workbench-case-${row.reportId}`}
                    >
                      <Table.Td>
                        <Text fw={500}>{row.codename ?? 'No codename'}</Text>
                        <Text size="xs" c="dimmed">
                          {teamNames[row.teamId] ?? row.teamId}
                          {row.playbook?.reportType ? ` · ${row.playbook.reportType}` : ''}
                          {row.playbook ? ` · ${row.playbook.name} v${row.playbook.version}` : ''}
                        </Text>
                        {row.copyOf ? (
                          <Badge
                            size="xs"
                            variant="light"
                            color="grape"
                            style={{ textTransform: 'none' }}
                            data-testid={`workbench-case-copy-of-${row.reportId}`}
                          >
                            Evaluation copy of {row.copyOf.codename ?? 'a case'}
                          </Badge>
                        ) : null}
                      </Table.Td>
                      <Table.Td>
                        <Stack gap={2}>
                          <Badge
                            size="sm"
                            variant="light"
                            color={chip.color}
                            style={{ textTransform: 'none', alignSelf: 'flex-start' }}
                          >
                            {chip.label}
                          </Badge>
                          {row.stageAt ? (
                            <Text size="xs" c="dimmed">
                              {formatWhen(row.stageAt)}
                            </Text>
                          ) : null}
                        </Stack>
                      </Table.Td>
                      <Table.Td>
                        <Due row={row} />
                      </Table.Td>
                      <Table.Td>
                        {row.needsYou > 0 ? (
                          <Group gap={6} wrap="nowrap">
                            <Badge size="sm" color="orange" variant="filled" circle>
                              {row.needsYou}
                            </Badge>
                            {row.blocking > 0 ? (
                              <Text size="xs" c="dimmed">
                                {row.blocking} holding a task
                              </Text>
                            ) : null}
                          </Group>
                        ) : (
                          <Text size="sm" c="dimmed">
                            —
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Run row={row} />
                      </Table.Td>
                      <Table.Td>
                        <Spend row={row} />
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>

            <Group justify="space-between">
              <Text size="sm" c="dimmed" data-testid="workbench-cases-count">
                {total === 0
                  ? 'No cases'
                  : `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} of ${total} case${total === 1 ? '' : 's'}`}
              </Text>
              {totalPages > 1 ? (
                <Pagination value={page} onChange={setPage} total={totalPages} />
              ) : null}
            </Group>
          </Stack>
        </LogoPaper>
      </Stack>
    </Container>
  );
}
